<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <div>
          <button @click="back" class="btn btn-sm btn-outline-secondary mb-2">
            <i class="mdi mdi-arrow-left mr-1"></i>{{ $t('common.back') }}
          </button>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.companyDetails') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.subtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0 d-flex gap-2">
          <router-link
            v-if="owner && owner.id"
            :to="{ name: 'EditOwner', params: { id: String(owner.id) } }"
            class="btn btn-outline-warning btn-sm px-3"
          >
            <i class="mdi mdi-pencil mr-1"></i>{{ $t('superadmin.editCompany') }}
          </router-link>
          <button
            @click="confirmDelete"
            class="btn btn-outline-danger btn-sm px-3 ml-2"
          >
            <i class="mdi mdi-delete-outline mr-1"></i>{{ $t('superadmin.deleteCompany') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Company Header Banner Card -->
    <div class="col-12 grid-margin">
      <div class="card bg-dark border-0 shadow-sm" style="border-radius: 12px; background: linear-gradient(135deg, #191c24 0%, #131720 100%);">
        <div class="card-body p-4">
          <div class="d-flex flex-wrap align-items-center">
            <div
              class="mr-4 mb-3 mb-md-0 d-flex align-items-center justify-content-center bg-black rounded"
              style="width: 80px; height: 80px; border: 1px solid rgba(255,255,255,0.1); overflow: hidden;"
            >
              <img
                v-if="owner && owner.company && owner.company.logo"
                :src="config.STORAGE_URL + owner.company.logo"
                alt="Logo"
                style="max-width: 100%; max-height: 100%; object-fit: contain;"
              />
              <i v-else class="mdi mdi-domain text-warning" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1">
              <div class="d-flex align-items-center flex-wrap">
                <h3 class="text-white font-weight-bold mb-1 mr-3">
                  {{ (owner && owner.company && owner.company.name) || (owner && owner.name) || '-' }}
                </h3>
                <span
                  class="badge px-3 py-1 font-weight-bold"
                  :class="isActive ? 'badge-success' : 'badge-danger'"
                >
                  <i class="mdi mr-1" :class="isActive ? 'mdi-check-circle' : 'mdi-alert-circle'"></i>
                  {{ isActive ? $t('superadmin.statusActive') : $t('superadmin.statusExpired') }}
                </span>
                <span v-if="healthData && healthData.is_expiring_soon" class="badge badge-warning ml-2 px-3 py-1">
                  {{ $t('superadmin.statusExpiring') }}
                </span>
              </div>
              <div class="text-muted small mt-1">
                <span class="mr-3"><i class="mdi mdi-account-tie mr-1 text-info"></i>{{ (owner && owner.name) || '-' }}</span>
                <span class="mr-3"><i class="mdi mdi-email-outline mr-1 text-primary"></i>{{ (owner && owner.email) || '-' }}</span>
                <span v-if="owner && owner.phone_number" class="mr-3"><i class="mdi mdi-phone mr-1 text-success"></i>{{ owner.phone_number }}</span>
                <span><i class="mdi mdi-calendar mr-1 text-warning"></i>{{ $t('superadmin.joinDate') }}: {{ formatDate(owner && owner.created_at) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content: Left Column Profile & Right Column Live Telemetry -->
    <div class="col-lg-6 col-12 grid-margin">
      <div class="card h-100 border-0 shadow-sm" style="background: #191c24; border-radius: 12px;">
        <div class="card-body p-4">
          <h4 class="text-white font-weight-bold mb-4 d-flex align-items-center">
            <i class="mdi mdi-office-building text-warning mr-2"></i>
            {{ $t('superadmin.companyInfo') }}
          </h4>

          <div class="table-responsive">
            <table class="table table-borderless text-light mb-0">
              <tbody>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0" style="width: 40%;">{{ $t('superadmin.companyName') }}</td>
                  <td class="font-weight-bold text-white py-3">
                    {{ (owner && owner.company && owner.company.name) || '-' }}
                  </td>
                </tr>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.ownerName') }}</td>
                  <td class="font-weight-bold text-white py-3">{{ (owner && owner.name) || '-' }}</td>
                </tr>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.registeredEmail') }}</td>
                  <td class="font-weight-bold text-info py-3">{{ (owner && owner.email) || '-' }}</td>
                </tr>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.phoneNumber') }}</td>
                  <td class="text-white py-3">{{ (owner && owner.phone_number) || '-' }}</td>
                </tr>
                <tr style="border-bottom: 1px solid rgba(255,255,255,0.05);">
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.joinDate') }}</td>
                  <td class="text-white py-3">{{ formatDate(owner && owner.created_at) }}</td>
                </tr>
                <tr>
                  <td class="text-muted py-3 pl-0">{{ $t('superadmin.playerQuotaUsage') }}</td>
                  <td class="py-3">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <span class="font-weight-bold text-warning">{{ totalUsersCount }} / {{ quotaLimit }} {{ $currentLocale === 'id' ? 'Pemain' : 'Players' }}</span>
                      <small class="text-muted">{{ quotaPercent }}%</small>
                    </div>
                    <div class="progress" style="height: 6px; background-color: rgba(255,255,255,0.08); border-radius: 4px;">
                      <div
                        class="progress-bar bg-warning"
                        role="progressbar"
                        :style="{ width: quotaPercent + '%' }"
                        :aria-valuenow="quotaPercent"
                        aria-valuemin="0"
                        aria-valuemax="100"
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Live Enterprise Health Telemetry -->
    <div class="col-lg-6 col-12 grid-margin">
      <div class="card h-100 border-0 shadow-sm" style="background: #191c24; border-radius: 12px;">
        <div class="card-body p-4">
          <div class="d-flex align-items-center justify-content-between mb-4">
            <h4 class="text-white font-weight-bold mb-0 d-flex align-items-center">
              <i class="mdi mdi-pulse text-success mr-2"></i>
              {{ $t('superadmin.companyTelemetry') }}
            </h4>
            <span
              v-if="healthStatusLabel"
              class="badge px-3 py-1 font-weight-bold"
              :class="healthBadgeClass"
            >
              {{ healthStatusLabel }}
            </span>
          </div>

          <!-- Key Metrics Grid -->
          <div class="row mb-3">
            <div class="col-6 mb-3">
              <div class="p-3 rounded" style="background: rgba(0, 210, 91, 0.08); border: 1px solid rgba(0, 210, 91, 0.2);">
                <div class="text-muted small text-uppercase">{{ $t('superadmin.activityRatio') }}</div>
                <div class="text-success font-weight-bold h3 mb-0 mt-1">
                  {{ (healthMetrics && healthMetrics.activity_ratio !== undefined) ? healthMetrics.activity_ratio + '%' : '0%' }}
                </div>
                <small class="text-muted">
                  {{ (healthMetrics && healthMetrics.active_users_7d) || 0 }} / {{ totalUsersCount }} {{ $t('superadmin.activeUsers7d') }}
                </small>
              </div>
            </div>

            <div class="col-6 mb-3">
              <div class="p-3 rounded" style="background: rgba(0, 144, 231, 0.08); border: 1px solid rgba(0, 144, 231, 0.2);">
                <div class="text-muted small text-uppercase">{{ $t('superadmin.completionSuccessRate') }}</div>
                <div class="text-info font-weight-bold h3 mb-0 mt-1">
                  {{ (healthMetrics && healthMetrics.completion_rate !== undefined) ? healthMetrics.completion_rate + '%' : '0%' }}
                </div>
                <small class="text-muted">
                  {{ (healthMetrics && healthMetrics.total_submissions) || 0 }} {{ $t('superadmin.questSubmissionsCount') }}
                </small>
              </div>
            </div>

            <div class="col-6 mb-3">
              <div class="p-3 rounded" style="background: rgba(255, 171, 0, 0.08); border: 1px solid rgba(255, 171, 0, 0.2);">
                <div class="text-muted small text-uppercase">{{ $t('superadmin.totalRulesConfigured') }}</div>
                <div class="text-warning font-weight-bold h3 mb-0 mt-1">
                  {{ (healthMetrics && healthMetrics.total_rules) || 0 }}
                </div>
                <small class="text-muted">{{ $currentLocale === 'id' ? 'Misi & Konsekuensi' : 'Quests & Consequences' }}</small>
              </div>
            </div>

            <div class="col-6 mb-3">
              <div class="p-3 rounded" style="background: rgba(235, 87, 87, 0.08); border: 1px solid rgba(235, 87, 87, 0.2);">
                <div class="text-muted small text-uppercase">{{ $t('superadmin.daysUntilExpiry') }}</div>
                <div class="font-weight-bold h3 mb-0 mt-1" :class="expiryDaysClass">
                  {{ formatExpiryDays }}
                </div>
                <small class="text-muted">
                  {{ (owner && owner.company && owner.company.expired_date) ? formatDate(owner.company.expired_date) : '-' }}
                </small>
              </div>
            </div>
          </div>

          <!-- Subscription Progress Bar -->
          <div class="p-3 rounded" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-light small font-weight-bold">{{ $t('superadmin.currentPlan') }}</span>
              <span class="badge badge-outline-warning">
                {{ (healthCompany && healthCompany.subscription && healthCompany.subscription.billing && healthCompany.subscription.billing.package && healthCompany.subscription.billing.package.package_name) || ($currentLocale === 'id' ? 'Standar Korporasi' : 'Enterprise Standard') }}
              </span>
            </div>
            <div class="text-muted small">
              {{ $t('superadmin.subtitle') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { getDetailOwner, deleteOwner } from '@/services/ownerService';
import { getSuperAdminCompanyHealth } from '@/services/superadminService';
import config from '@/config/config';
import Swal from 'sweetalert2';

export default {
  name: 'OwnerDetailView',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      owner: null,
      healthData: null,
      config,
      loading: true,
    };
  },
  computed: {
    isActive() {
      if (!this.owner || !this.owner.company) return true;
      return this.owner.company.is_active !== 0;
    },
    totalUsersCount() {
      if (this.healthMetrics && this.healthMetrics.total_users !== undefined) {
        return this.healthMetrics.total_users;
      }
      return (this.owner && this.owner.player_count) || 0;
    },
    quotaLimit() {
      return (this.owner && this.owner.company && this.owner.company.number_of_players) || 100;
    },
    quotaPercent() {
      if (!this.quotaLimit) return 0;
      return Math.min(100, Math.round((this.totalUsersCount / this.quotaLimit) * 100));
    },
    healthMetrics() {
      return this.healthData && this.healthData.metrics;
    },
    healthCompany() {
      return this.healthData && this.healthData.company;
    },
    healthStatusLabel() {
      if (!this.healthData) return '';
      const st = this.healthData.health_status;
      if (st === 'healthy') return this.$currentLocale === 'id' ? 'Platform Sehat' : 'Platform Healthy';
      if (st === 'warning_subscription') return this.$currentLocale === 'id' ? 'Segera Berakhir' : 'Plan Expiring';
      if (st === 'warning_low_activity') return this.$currentLocale === 'id' ? 'Aktivitas Rendah' : 'Low Engagement';
      return st;
    },
    healthBadgeClass() {
      if (!this.healthData) return 'badge-secondary';
      const st = this.healthData.health_status;
      if (st === 'healthy') return 'badge-success';
      if (st === 'warning_subscription') return 'badge-warning';
      if (st === 'warning_low_activity') return 'badge-danger';
      return 'badge-info';
    },
    formatExpiryDays() {
      if (!this.healthMetrics || this.healthMetrics.days_until_expiry === null) {
        return this.$currentLocale === 'id' ? 'Seumur Hidup' : 'Lifetime';
      }
      const days = this.healthMetrics.days_until_expiry;
      if (days < 0) return this.$currentLocale === 'id' ? `Lewat ${Math.abs(days)}h` : `${Math.abs(days)}d Overdue`;
      return this.$currentLocale === 'id' ? `${days} Hari` : `${days} Days`;
    },
    expiryDaysClass() {
      if (!this.healthMetrics || this.healthMetrics.days_until_expiry === null) return 'text-white';
      const days = this.healthMetrics.days_until_expiry;
      if (days < 0) return 'text-danger';
      if (days <= 7) return 'text-warning';
      return 'text-success';
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        const resp = await getDetailOwner(this.id);
        this.owner = resp;

        const companyId = resp.company_id || (resp.company && resp.company.id);
        if (companyId) {
          try {
            const healthResp = await getSuperAdminCompanyHealth(companyId);
            this.healthData = healthResp.data || healthResp;
          } catch (e) {
            console.warn('Could not load company health telemetry:', e);
          }
        }
      } catch (err) {
        console.error('Error loading owner details:', err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMMM YYYY');
    },
    back() {
      this.$router.go(-1);
    },
    confirmDelete() {
      Swal.fire({
        title: this.$t('superadmin.deleteCompany'),
        text: this.$t('superadmin.confirmDeleteCompany'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: this.$t('common.delete'),
        cancelButtonText: this.$t('common.cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          deleteOwner(this.id).then(() => {
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$currentLocale === 'id' ? 'Perusahaan telah dinonaktifkan/dihapus.' : 'Enterprise has been offboarded.',
              confirmButtonColor: '#3085d6',
            }).then(() => {
              this.$router.push({ name: 'ListOwner' });
            });
          });
        }
      });
    },
  },
};
</script>
